import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, IRowNode, ValueFormatterParams } from 'ag-grid-community';

import { TextButtonCell, TooltipTextCell } from '~/components/common';

import { formatDate } from '~/utils/date';

import {
  DATE_FORMAT,
  DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR,
  DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR,
} from '~/constants';

import type { DeliveryResultReportItem } from '~/types/delivery-agency.types';

import styles from './getDeliveryAgencyReportsColumnDefs.module.scss';

const getDeliveryAgencyReportsColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail: (report: DeliveryResultReportItem) => void;
}) => {
  const columnDefs: ColDef<DeliveryResultReportItem>[] = [
    {
      colId: 'rowNumber',
      headerName: 'No',
      valueGetter: ({ node }) => (node?.rowIndex ?? 0) + 1 + currentPage * pageSize,
      width: 72,
    },
    {
      field: 'outboundCode',
      headerName: '출고번호',
      width: 160,
    },
    {
      field: 'clientOrderCodes',
      headerName: '판매처 주문번호',
      width: 160,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<DeliveryResultReportItem, DeliveryResultReportItem['clientOrderCodes']>) =>
        value?.length ? value.join(', ') : '-',
      cellRenderer: TooltipTextCell,
    },
    {
      field: 'shippingLabelNumber',
      headerName: '운송장 번호',
      width: 232,
    },
    {
      colId: 'viewDetail',
      headerName: '',
      minWidth: 80,
      width: 80,
      cellRenderer: TextButtonCell,
      cellRendererParams: {
        children: '상세보기',
        showDashIfNoValue: false,
        onClick: ({ data }: IRowNode<DeliveryResultReportItem>) => {
          if (!data) return;
          onClickViewDetail(data);
        },
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'estimatedDeliveryDate',
      headerName: '배송예정일',
      width: 100,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<DeliveryResultReportItem, DeliveryResultReportItem['estimatedDeliveryDate']>) =>
        formatDate(value, DATE_FORMAT.DATE) ?? '-',
    },
    {
      field: 'deliveryType',
      headerName: '배송 유형',
      width: 96,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<DeliveryResultReportItem, DeliveryResultReportItem['deliveryType']>) =>
        value?.description || '-',
    },
    {
      field: 'reportedShippingLabelStatus',
      headerName: '운송장 상태',
      cellDataType: false,
      width: 105,
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<DeliveryResultReportItem, DeliveryResultReportItem['reportedShippingLabelStatus']>) => ({
        variant: 'light',
        status: true,
        color: value ? DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR[value.code] : undefined,
        children: value?.description || '-',
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'reportedDeliveryResults',
      headerName: '배송결과',
      cellDataType: false,
      width: 185,
      cellRenderer: ({
        value,
      }: ICellRendererParams<DeliveryResultReportItem, DeliveryResultReportItem['reportedDeliveryResults']>) => {
        if (!value?.length) return null;

        return (
          <div className={styles['delivery-results-cell']}>
            {value.map((result) => (
              <Badge
                key={result.code}
                size="small"
                variant="light"
                color={DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR[result.code]}
              >
                {result.description}
              </Badge>
            ))}
          </div>
        );
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'deliveryLocation',
      headerName: '요청 장소',
      width: 76,
    },
    {
      field: 'deliveryLocationDetail',
      headerName: '요청 장소 상세',
      width: 170,
      cellRenderer: TooltipTextCell,
    },
    {
      field: 'actualDeliveryLocationType',
      headerName: '처리 장소',
      width: 122,
    },
    {
      field: 'deliveryFailureReasonCategory',
      headerName: '배송 실패 사유',
      width: 170,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<DeliveryResultReportItem, DeliveryResultReportItem['deliveryFailureReasonCategory']>) =>
        value?.description || '-',
    },
    {
      field: 'changedDeliveryMethodType',
      headerName: '타 장소에 배송한 사유',
      width: 186,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<DeliveryResultReportItem, DeliveryResultReportItem['changedDeliveryMethodType']>) =>
        value?.description || '-',
    },
    {
      field: 'entranceMethod',
      headerName: '출입 방법',
      width: 160,
    },
    {
      field: 'receiverName',
      headerName: '수령자',
      width: 100,
    },
    {
      field: 'refineFullAddress',
      headerName: '정제 주소',
      flex: 1,
      minWidth: 252,
      width: 252,
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyReportsColumnDefs;
